import { useEffect, useRef, useState } from "react";

/**
 * Detects whether a `-webkit-line-clamp`'d element truncates its text.
 *
 * `scrollHeight` is unreliable for line-clamp — the overflowing lines are
 * dropped at layout time, so `scrollHeight === clientHeight`. Instead this
 * measures the natural height with the clamp briefly lifted.
 *
 * Attach the returned `ref` to the clamped element; `isTruncated` reflects
 * whether its content currently overflows the clamp.
 */
export const useClampTruncation = <T extends HTMLElement>(
  /** Re-measure when this changes — typically the clamped text. */
  dependency: unknown,
) => {
  const ref = useRef<T>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let active = true;
    const measure = () => {
      if (!active) return;
      const clampedHeight = el.clientHeight;
      el.style.setProperty("-webkit-line-clamp", "none");
      const fullHeight = el.clientHeight;
      el.style.removeProperty("-webkit-line-clamp");
      setIsTruncated(fullHeight - clampedHeight > 1);
    };
    measure();
    document.fonts.ready.then(measure);
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => {
      active = false;
      observer.disconnect();
    };
  }, [dependency]);

  return { ref, isTruncated };
};
